export const hero = {
  eyebrow: "BFSG-konform in 4 Schritten",
  headlineLines: ["Plug in.", "Barrierefrei.", "Einfach Fertig."],
  subline:
    "ClarusPlus prüft Ihre Website täglich, erstellt Berichte und führt Sie Schritt für Schritt zur vollen BFSG-Konformität — automatisch.",
  trustMarks: [
    "Kreditkarte nicht nötig",
    "DSGVO-konform",
    "Marktweit günstigstes Angebot",
  ],
  cta: { label: "Website jetzt scannen", href: "/scan" },
} as const;

export const trustLogos = {
  heading: "Über 200 Websites vertrauen ClarusPlus:",
  logos: [
    { name: "Logic", file: "logic.png" },
    { name: "Tapline", file: "tapline.png" },
    { name: "Pera", file: "pera.png" },
    { name: "Unicoin", file: "unicoin.png" },
    { name: "Aura", file: "aura.png" },
    { name: "unify", file: "unify.png" },
  ],
} as const;

export const problemBlock = {
  heading: "BFSG gilt seit Juni 2025, für fast jede Website.",
  intro: "Handeln Sie jetzt — oder riskieren Sie diese Konsequenzen:",
  /**
   * Card body supports `**word**` markdown-style bold highlights, rendered as
   * `<strong>` inline. Keeps the brand emphasis pattern visible in the source.
   */
  cards: [
    {
      icon: "alert-triangle",
      title: "Hohe Bußgelder",
      body: "Bis zu **€100.000** pro Verstoß möglich. Behörden können Bußgelder ohne Vorwarnung verhängen.",
    },
    {
      icon: "scale",
      title: "Abmahnrisiko",
      body: "Verbände und Behindertenorganisationen sind zur **Klage** berechtigt — direkt gegen Ihren Betrieb.",
    },
    {
      icon: "trending-down",
      title: "Reputationsverlust",
      body: "Nicht barrierefreie Websites schließen **Millionen** von Menschen aus und schaden der Marke nachhaltig.",
    },
    {
      icon: "log-out",
      title: "Kundenabwanderung",
      body: "Öffentliche Aufträge und B2G-Geschäfte setzen **BFSG**-Konformität zwingend voraus.",
    },
  ],
} as const;

export const stepsBlock = {
  heading: "In 4 Schritten zur Konformität.",
  subline: "Kein technisches Wissen nötig — ClarusPlus führt Sie durch alles.",
  steps: [
    {
      number: 1,
      title: "URL eingeben",
      body: "Geben Sie die Adresse Ihrer Website ein. Kein Konto, keine Installation.",
      icon: "link",
    },
    {
      number: 2,
      title: "Scan starten",
      body: "Automatische Prüfung nach WCAG 2.2 AA — 22 Prüfpunkte werden analysiert.",
      icon: "scan",
    },
    {
      number: 3,
      title: "Bericht erhalten",
      body: "Detaillierter Report mit priorisierten Fehlern und konkreten Lösungsvorschlägen.",
      icon: "file-text",
    },
    {
      number: 4,
      title: "Plug in",
      body: "Ein einziger Code-Schnipsel genügt. ClarusPlus integriert sich nahtlos in jede Website.",
      icon: "plug",
    },
  ],
  cta: { label: "Jetzt 7 Tage kostenlos testen", href: "/scan" },
} as const;

export const featureToolkit = {
  heading: "Vollständiges Toolkit für Barrierefreiheit",
  subline:
    "Unser Widget bietet ein umfassendes Paket von Zugänglichkeitswerkzeugen, um Ihre Website inklusiver zu gestalten.",
  features: [
    {
      title: "Text-Einstellungen",
      body: "Ändern Sie die Textgröße oder die Abstände, um die Lesbarkeit für Benutzer mit Sehbehinderungen zu verbessern.",
      icon: "type",
    },
    {
      title: "Textlupe",
      body: "Vergrößern Sie bestimmte Texte der Seite, um Nutzern zu helfen, die größere Inhalte benötigen.",
      icon: "zoom-in",
    },
    {
      title: "Farbeinstellungen",
      body: "Erhöhen Sie den Kontrast zwischen Text und Hintergrund, um die Lesbarkeit für farbenblinde und sehbehinderte Nutzer zu verbessern.",
      icon: "palette",
    },
    {
      title: "Fokus-Modus",
      body: "Verringert Ablenkung, indem nur der relevante Inhalt hervorgehoben wird.",
      icon: "focus",
    },
    {
      title: "Links hervorheben",
      body: "Heben Sie alle Links auf der Seite deutlich hervor, damit sie leichter zu finden sind.",
      icon: "link-2",
    },
    {
      title: "Schwarz-Weiß-Filter",
      body: "Wandeln Sie die Farben der Seite in Graustufen um, um Kontraste zu reduzieren oder zu betonen.",
      icon: "contrast",
    },
    {
      title: "Bilder ausblenden",
      body: "Blenden Sie alle Bilder aus, um die Seite textfokussierter und weniger ablenkend zu machen.",
      icon: "image-off",
    },
    {
      title: "Lichtbalken",
      body: "Fügen Sie einen Lichtbalken hinzu, um die aktuelle Textzeile visuell hervorzuheben.",
      icon: "highlighter",
    },
    {
      title: "Element hervorheben",
      body: "Heben Sie ausgewählte Elemente auf der Seite hervor, um sie deutlicher sichtbar zu machen.",
      icon: "square-dashed",
    },
    {
      title: "Font Family anpassen",
      body: "Passen Sie die Schriftart der Texte an, um eine besser lesbare Typografie zu erhalten.",
      icon: "case-sensitive",
    },
    {
      title: "Großer Zeiger",
      body: "Vergrößern Sie den Mauszeiger, um ihn besser sichtbar zu machen.",
      icon: "mouse-pointer-2",
    },
    {
      title: "Virtuelle Tastatur",
      body: "Zeigen Sie eine Bildschirmtastatur an, die Nutzern beim Tippen hilft.",
      icon: "keyboard",
    },
    {
      title: "Sound muten",
      body: "Schalten Sie alle Sounds und Töne der Webseite stumm, um Ablenkungen zu vermeiden.",
      icon: "volume-x",
    },
    {
      title: "Texte vorlesen",
      body: "Lassen Sie sich den Text auf der Seite vorlesen, um die Inhalte akustisch zu erfassen.",
      icon: "volume-2",
    },
    {
      title: "Kontrast erhöhen",
      body: "Erhöhen Sie den Kontrast der Seite, um Inhalte besser sichtbar zu machen.",
      icon: "sun",
    },
    {
      title: "Animationen deaktivieren",
      body: "Stoppen Sie alle Animationen auf der Seite, um empfindliche Nutzer zu entlasten.",
      icon: "circle-pause",
    },
    {
      title: "Wortabstand anpassen",
      body: "Erhöhen oder verringern Sie den Abstand zwischen Wörtern für bessere Lesbarkeit.",
      icon: "move-horizontal",
    },
    {
      title: "Zeilenabstand anpassen",
      body: "Ändern Sie den Abstand zwischen den Zeilen, um das Lesen zu erleichtern.",
      icon: "align-justify",
    },
    {
      title: "Schriftgröße anpassen",
      body: "Passen Sie die Größe der Texte individuell an, um sie besser erkennen zu können.",
      icon: "a-large-small",
    },
    {
      title: "Hintergrundfarbe ändern",
      body: "Ändern Sie die Hintergrundfarbe der Seite, um eine angenehmere Ansicht zu erzielen.",
      icon: "paint-bucket",
    },
    {
      title: "Textfarbe ändern",
      body: "Ändern Sie die Farbe des Textes, um den Kontrast und die Lesbarkeit zu verbessern.",
      icon: "paintbrush",
    },
    {
      title: "Titel-Farbe ändern",
      body: "Passen Sie die Farben der Überschriften an, um sie stärker hervorzuheben.",
      icon: "heading-1",
    },
    {
      title: "Interaktive Farben anpassen",
      body: "Ändern Sie die Farben von interaktiven Elementen wie Buttons und Links.",
      icon: "mouse-pointer-click",
    },
  ],
} as const;

export const protectionBlock = {
  heading: "So schützt ClarusPlus Ihre Website",
  subline:
    "Mit ClarusPlus erfüllen Sie alle gesetzlichen Anforderungen automatisch und machen Ihre Website einfach und zuverlässig BFSG-konform.",
  cards: [
    {
      icon: "circle-check",
      title: "100% Gesetzeskonformität",
      body: "ClarusPlus erfüllt alle Anforderungen des BFSG, der WCAG 2.2, der BITV 2.0 und der EN 301 549. Sie sind automatisch auf der sicheren Seite.",
      points: [
        "WCAG 2.2 Level AA konform",
        "BFSG-Compliance garantiert",
        "BITV 2.0 Anforderungen erfüllt",
        "EN 301 549 Anforderungen erfüllt",
      ],
      tone: "purple",
    },
    {
      icon: "eye",
      title: "Automatische Überwachung",
      body: "ClarusPlus prüft Ihre Website kontinuierlich und behebt Barrieren automatisch. Sie bleiben dauerhaft compliant – ohne manuellen Aufwand.",
      points: [
        "24/7 automatische Überwachung",
        "Sofortige Fehlerbehebung",
        "Regelmäßige Compliance-Reports",
      ],
      tone: "rose",
    },
    {
      icon: "blocks",
      title: "Einfache Integration",
      body: "Ein einziger Code-Schnipsel genügt. ClarusPlus integriert sich nahtlos in jede Website – ohne Programmieraufwand oder technische Hürden.",
      points: [
        "Installation in unter 5 Minuten",
        "Keine technischen Vorkenntnisse nötig",
      ],
      tone: "blue",
    },
    {
      icon: "lock",
      title: "DSGVO-konform & sicher",
      body: "Alle Daten bleiben in Deutschland. ClarusPlus ist vollständig DSGVO-konform und schützt die Privatsphäre Ihrer Nutzer.",
      points: [
        "Server-Standort Deutschland",
        "100% DSGVO-konform",
        "Höchste Datenschutz-Standards",
      ],
      tone: "green",
    },
  ],
} as const;

/**
 * Pricing matches the original clarusplus.com tiers (see lurelink/clarus/homepage).
 *
 * Yearly billing is discounted ~10 % off the un-discounted 12× monthly price
 * (shown struck out). The toggle badge shows a literal "X" placeholder (−X %)
 * pending the final headline number:
 *   - Basic:    monthly 14,90 → yearly 159   (before 178,80, −11,1 %)
 *   - Pro:      monthly 39,90 → yearly 429   (before 478,80, −10,4 %)
 *   - Business: monthly 159,90 → yearly 1.724 (before 1.918,80, −10,2 %)
 *
 * Each tier lists its scope as icon chips under the price (`limits`): the
 * website count and the employee range it is sized for (Basic 1–15, Pro 16–25,
 * Business 26–50; Enterprise from 51 lives in the footnote link).
 *
 * Prices are strings so the German thousands separator / decimal comma render
 * correctly (JS number formatting would drop trailing zeros: 14.90 → "14.9").
 */
export const pricingBlock = {
  heading: "Transparent. Skalierbar. Fair.",
  subline: "Kein Kleingedrucktes. Jederzeit kündbar. Rechtssicher.",
  toggle: {
    monthly: "Monatlich",
    yearly: "Jährlich − X%",
  },
  tiers: [
    {
      name: "Basic",
      monthlyPrice: "14,90",
      yearlyTotal: "159",
      yearlyTotalBefore: "178,80",
      currency: "€",
      cadence: "pro Monat",
      limits: [
        { icon: "language", label: "1 Website" },
        { icon: "group", label: "1–15 Mitarbeiter" },
      ],
      featured: false,
      cta: {
        label: "Kostenlos starten",
        href: "https://dashboard.clarusplus.com/auth/register?plan=basic",
      },
      features: [
        { label: "1 Onlineshop / Webseite", icon: "check" },
        { label: "§BFSG Konform", icon: "check" },
        { label: "WCAG 2.2 Konform", icon: "check" },
        { label: "1 Audit inkl. Berichtdownload", icon: "check" },
        { label: "Zugang zur Webseitenverwaltung", icon: "check" },
      ],
    },
    {
      name: "Pro",
      monthlyPrice: "39,90",
      yearlyTotal: "429",
      yearlyTotalBefore: "478,80",
      currency: "€",
      cadence: "pro Monat",
      limits: [
        { icon: "language", label: "20 Websites" },
        { icon: "group", label: "16–25 Mitarbeiter" },
      ],
      featured: true,
      badge: "Empfohlen",
      cta: {
        label: "Jetzt Pro starten",
        href: "https://dashboard.clarusplus.com/auth/register?plan=pro",
      },
      features: [
        { label: "Basic komplett", icon: "check" },
        { label: "19 Onlineshops / Webseiten", icon: "plus" },
        { label: "19 Audits inkl. Berichtdownload", icon: "plus" },
        { label: "15 Min. Beratungsgespräch monatlich", icon: "plus" },
        {
          label: "Zugang zum Generator für die Barrierefreiheitserklärung",
          icon: "plus",
        },
      ],
    },
    {
      name: "Business",
      monthlyPrice: "159,90",
      yearlyTotal: "1.724",
      yearlyTotalBefore: "1.918,80",
      currency: "€",
      cadence: "pro Monat",
      limits: [
        {
          icon: "language",
          // "∞" keeps the chip row on one line at every card width; the
          // screen-reader label spells it out.
          label: "∞ Websites",
          srLabel: "Unbegrenzt viele Websites",
        },
        { icon: "group", label: "26–50 Mitarbeiter" },
      ],
      featured: false,
      cta: { label: "Kontakt aufnehmen", href: "https://clarusplus.com/meeting" },
      features: [
        { label: "Pro komplett", icon: "check" },
        { label: "Unbegrenzt Onlineshops / Webseiten", icon: "plus" },
        { label: "Dedizierter Account-Manager", icon: "plus" },
        { label: "Bis zu 1.000.000 Webseitenaufrufe", icon: "plus" },
        { label: "24/7 technischer Support", icon: "plus" },
      ],
    },
  ],
  footnote:
    "Alle Preise zzgl. MwSt. · 7 Tage kostenlos testen · Jederzeit kündbar",
  footnoteLinkLabel: "Enterprise-Tarif ab 51 Mitarbeitern auf Anfrage",
  footnoteLinkHref: "/kontakt",
} as const;

export const testimonials = {
  heading: "Was unsere Kunden sagen:",
  subline: "Über 200 zufriedene Websites — und täglich mehr.",
  items: [
    {
      quote:
        "ClarusPlus hat uns von Anfang an durch eine einfache Integration, zuverlässige Funktionalität und einen sehr persönlichen Support überzeugt. Besonders schätzen wir, dass das Team offen für Feedback ist und die Lösung kontinuierlich weiterentwickelt.",
      name: "Cedrik Güldenstein",
      role: "Postebote UG",
      href: "https://www.postebote.marketing/",
      rating: 5,
    },
    {
      quote:
        "Barrierefreiheit ist Pflicht – die Lösung einfacher als gedacht. ClarusPlus hat meine Kunden überzeugt: schnell integriert, spürbar hilfreich und ohne Eingriff in den Code. BFSG-konform, zudem SEO-freundlich.",
      name: "Andreas Pfeifer",
      role: "Die Heldenhelfer GmbH",
      rating: 5,
    },
    {
      quote:
        "Als Sicherheitsdienstleister können wir uns rechtliche Grauzonen nicht leisten. ClarusPlus prüft unsere Website täglich und dokumentiert die BFSG-Konformität lückenlos — genau die Sicherheit, die wir auch unseren eigenen Kunden versprechen.",
      name: "Tetyana Heß",
      role: "T.S.H. Security GmbH",
      rating: 5,
    },
    {
      quote:
        "Wir bauen und sanieren — mit Websites kennen wir uns nicht aus. Genau deshalb war ClarusPlus ideal: ein Schnipsel eingefügt, fertig. Kein Programmierer, kein Aufwand und trotzdem barrierefrei.",
      name: "Miroslav Boska",
      role: "Legatum Bau und Service GmbH",
      rating: 5,
    },
    {
      quote:
        "Die täglichen Berichte zeigen uns schwarz auf weiß, wo wir stehen. Statt einer einmaligen Prüfung bleiben wir dauerhaft konform, ohne dass jemand bei uns ständig nachkontrollieren muss.",
      name: "Steffen Gontcharenko",
      role: "LTM Service GmbH",
      rating: 5,
    },
    {
      quote:
        "In unter zehn Minuten eingebunden und sofort wirksam. Der erste Scan hat Barrieren gefunden, von denen wir nichts ahnten — verständlich erklärt und Schritt für Schritt behoben.",
      name: "Andrej Zhavarankau",
      role: "Rotkamp GmbH",
      rating: 5,
    },
    {
      quote:
        "Barrierefreiheit ist für uns kein Pflichtprogramm, sondern erschließt neue Nutzergruppen. ClarusPlus macht das bezahlbar und unkompliziert — gerade für ein Unternehmen unserer Größe genau richtig.",
      name: "Impuls Play GmbH",
      role: "",
      rating: 5,
    },
    {
      quote:
        "Schnell eingebunden, sofort spürbar — und endlich BFSG-konform, ohne Aufwand auf unserer Seite. Genau so muss das sein.",
      name: "Nick Defever",
      role: "Myfiliate UG",
      rating: 5,
    },
  ],
} as const;

export const faqBlock = {
  heading: "Häufige Fragen.",
  items: [
    {
      q: "Gilt das BFSG für meine Website?",
      a: "Das Barrierefreiheitsstärkungsgesetz gilt seit Juni 2025 für nahezu alle Websites mit B2C-Angeboten — Onlineshops, Buchungsstrecken, Banking, Reise- und Ticketing-Plattformen sowie digitale Services. Auch B2B-Plattformen, die Endkund:innen Zugang gewähren, sind betroffen. Im Zweifel: lieber konform sein als haften.",
    },
    {
      q: "Brauche ich technische Kenntnisse?",
      a: "Nein. Ein einziger Code-Schnipsel reicht — Sie kopieren ihn in den <head> oder über Ihren Tag-Manager. Keine Entwickler:innen-Stunde, kein Build, kein Hosting-Setup. Wenn Sie schon einmal ein Google-Analytics-Snippet eingebaut haben, schaffen Sie auch das.",
    },
    {
      q: "Was passiert bei Verstößen?",
      a: "Behörden können Bußgelder von bis zu €100.000 pro Verstoß verhängen — ohne Vorwarnung. Verbände und Behindertenorganisationen sind zusätzlich klageberechtigt und nutzen das Recht aktiv. Hinzu kommen Abmahnkosten, Reputationsschäden und Umsatzverluste durch Nutzer:innen, die Ihre Seite nicht bedienen können.",
    },
    {
      q: "Was ist im Overlay enthalten?",
      a: "Über 22 Zugänglichkeits-Tools: anpassbare Schriftgröße, Textlupe, Farb- und Kontrastfilter, Fokus-Modus, hervorgehobene Links, virtuelle Tastatur, Screen-Reader-Unterstützung, Zeilen- und Wortabstand, S/W-Filter, Bilder ausblenden, Animationen deaktivieren und mehr. Alle Tools sind tastatur- und screenreader-bedienbar.",
    },
    {
      q: "Wie schnell bin ich konform?",
      a: "Das Overlay wirkt unmittelbar nach Einbindung — in der Regel sind Sie innerhalb von 5 bis 15 Minuten messbar konformer. Der vollständige Compliance-Status (inkl. Berichten und Zertifikat) hängt von der Anzahl der Unterseiten ab und wird kontinuierlich durch unsere täglichen Scans verbessert.",
    },
    {
      q: "Gibt es eine kostenlose Testversion?",
      a: "Ja. Sie können ClarusPlus 7 Tage kostenlos und ohne Risiko testen — keine Kreditkarte, keine Einrichtungsgebühr, jederzeit kündbar. Der erste Scan ist sogar ohne Konto möglich; einfach URL eingeben.",
    },
  ],
} as const;

export const finalCta = {
  heading: "Starten Sie jetzt. Kostenlos.",
  subline: "Ihr erster Scan dauert 60 Sekunden. Kein Risiko, keine Kreditkarte.",
  cta: { label: "Website jetzt scannen", href: "/scan" },
  secondaryCta: { label: "Demo buchen", href: "https://clarusplus.com/meeting" },
  trustMarks:
    "Kreditkarte nicht erforderlich · DSGVO-konform · Jederzeit kündbar",
} as const;
